/**
 * Vehicle Collection - Filtered Product Display
 * Reads URL params (year, make, model) and displays compatible products from Firebase
 */

(function() {
  'use strict';

  // Firebase Configuration (same as vehicle-selector.js)
  const FIREBASE_CONFIG = {
    apiKey: "AIzaSyCng7cVfi_qPkFckaXZ96w40vQ0XoKhQro",
    authDomain: "leitner-designs.firebaseapp.com",
    databaseURL: "https://leitner-designs-fitment.firebaseio.com",
    projectId: "leitner-designs",
    storageBucket: "leitner-designs.firebasestorage.app",
    messagingSenderId: "527135358298",
    appId: "1:527135358298:web:911d1696da219e37d48763"
  };

  // Local storage key for saved vehicles
  const STORAGE_KEY = 'leitner_saved_vehicles';

  // Cache for Firebase data
  let dataCache = {
    years: null,
    makes: null,
    vehicles: null,
    racks: null
  };

  let db = null;

  /**
   * Get saved vehicles from localStorage
   */
  function getSavedVehicles() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Get display name for a vehicle (nickname or Year Make Model)
   */
  function getVehicleDisplayName(year, make, model) {
    const vehicles = getSavedVehicles();
    const match = vehicles.find(v =>
      v.year === year &&
      v.make?.toLowerCase() === make?.toLowerCase() &&
      v.model?.toLowerCase() === model?.toLowerCase()
    );
    return match?.name || `${year} ${make} ${model}`;
  }

  /**
   * Initialize Firebase
   */
  async function initFirebase() {
    if (db) return db;

    if (typeof firebase === 'undefined') {
      await loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-app-compat.js');
      await loadScript('https://www.gstatic.com/firebasejs/9.22.0/firebase-database-compat.js');
    }

    if (!firebase.apps.length) {
      firebase.initializeApp(FIREBASE_CONFIG);
    }

    db = firebase.database();
    return db;
  }

  /**
   * Load external script
   */
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Fetch data from Firebase with caching
   */
  async function fetchData(path) {
    const cacheKey = path.replace('/', '');
    if (dataCache[cacheKey]) {
      return dataCache[cacheKey];
    }

    try {
      await initFirebase();
      const snapshot = await db.ref(path).once('value');
      const data = snapshot.val();
      dataCache[cacheKey] = data;
      return data;
    } catch (error) {
      console.error(`Error fetching ${path}:`, error);
      throw error;
    }
  }

  /**
   * Get URL parameters
   */
  function getUrlParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      year: params.get('year'),
      make: params.get('make'),
      model: params.get('model')
    };
  }

  /**
   * Update URL with vehicle params
   */
  function updateUrl(year, make, model) {
    const url = new URL(window.location.href);

    if (year) url.searchParams.set('year', year);
    else url.searchParams.delete('year');

    if (make) url.searchParams.set('make', make);
    else url.searchParams.delete('make');

    if (model) url.searchParams.set('model', model);
    else url.searchParams.delete('model');

    window.history.pushState({}, '', url);
  }

  /**
   * Get compatible product handles for a vehicle
   */
  async function getCompatibleProducts(year, make, model) {
    const vehicles = await fetchData('/vehicles');
    const racks = await fetchData('/racks');

    if (!vehicles || !racks) {
      return [];
    }

    // Find matching vehicle
    const matchingVehicle = Object.entries(vehicles).find(([id, vehicle]) => {
      return vehicle.year === year &&
             vehicle.make?.toLowerCase() === make?.toLowerCase() &&
             vehicle.model?.toLowerCase() === model?.toLowerCase();
    });

    if (!matchingVehicle) {
      return [];
    }

    const [vehicleId, vehicleData] = matchingVehicle;
    const compatibleRackIds = vehicleData.compatibleRacks || [];

    // Get product handles/IDs for compatible racks
    const products = [];
    for (const rackId of compatibleRackIds) {
      const rack = racks[rackId];
      if (rack) {
        products.push({
          handle: rack.shopifyHandle,
          productId: rack.shopifyProductId,
          rackId: rackId
        });
      }
    }

    return products;
  }

  /**
   * Fetch product data from Shopify
   */
  async function fetchProductByHandle(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error(`Error fetching product ${handle}:`, error);
      return null;
    }
  }

  /**
   * Generate year range
   */
  function generateYearRange(min, max) {
    const years = [];
    for (let year = max; year >= min; year--) {
      years.push(year.toString());
    }
    return years;
  }

  /**
   * Initialize the vehicle collection
   */
  async function initVehicleCollection(container) {
    const vehicleInfoEl = container.querySelector('[data-vehicle-info]');
    const vehicleDisplayEl = container.querySelector('[data-vehicle-display]');
    const loadingEl = container.querySelector('[data-loading]');
    const emptyEl = container.querySelector('[data-empty]');
    const productGridEl = container.querySelector('[data-product-grid]');
    const allProductsEl = container.querySelector('[data-all-products]');

    // Filter elements
    const yearSelect = container.querySelector('[data-filter-year]');
    const makeSelect = container.querySelector('[data-filter-make]');
    const modelSelect = container.querySelector('[data-filter-model]');
    const applyBtn = container.querySelector('[data-filter-apply]');

    // Show/hide helpers
    function showLoading(show) {
      if (loadingEl) loadingEl.hidden = !show;
    }

    function showEmpty(show) {
      if (emptyEl) emptyEl.hidden = !show;
    }

    function showProductGrid(show) {
      if (productGridEl) productGridEl.hidden = !show;
    }

    function showAllProducts(show) {
      if (allProductsEl) allProductsEl.hidden = !show;
    }

    function showVehicleInfo(show, vehicleText = '') {
      if (vehicleInfoEl) vehicleInfoEl.hidden = !show;
      if (vehicleDisplayEl && vehicleText) vehicleDisplayEl.textContent = vehicleText;
    }

    /**
     * Render product card HTML
     */
    function renderProductCard(product) {
      const priceHtml = product.compare_at_price > product.price
        ? `<span class="vehicle-collection__price--sale">${formatMoney(product.price)}</span>
           <span class="vehicle-collection__price--compare">${formatMoney(product.compare_at_price)}</span>`
        : `<span class="vehicle-collection__price">${formatMoney(product.price)}</span>`;

      const imageHtml = product.featured_image
        ? `<img src="${product.featured_image}" alt="${product.title}" loading="lazy">`
        : `<div class="vehicle-collection__product-placeholder">
             <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
               <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
               <circle cx="8.5" cy="8.5" r="1.5"/>
               <polyline points="21 15 16 10 5 21"/>
             </svg>
           </div>`;

      return `
        <div class="vehicle-collection__product-card" data-product-id="${product.id}" data-product-handle="${product.handle}">
          <span class="vehicle-collection__product-badge">Compatible</span>
          <a href="/products/${product.handle}" class="vehicle-collection__product-link">
            <div class="vehicle-collection__product-image">
              ${imageHtml}
            </div>
            <div class="vehicle-collection__product-info">
              <h3 class="vehicle-collection__product-title">${product.title}</h3>
              <div class="vehicle-collection__product-price">
                ${priceHtml}
              </div>
            </div>
          </a>
        </div>
      `;
    }

    /**
     * Format money (simple cents to dollars)
     */
    function formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    }

    /**
     * Load and display filtered products
     */
    async function loadFilteredProducts(year, make, model) {
      showAllProducts(false);
      showEmpty(false);
      showProductGrid(false);
      showLoading(true);
      showVehicleInfo(true, getVehicleDisplayName(year, make, model));

      try {
        const compatibleProducts = await getCompatibleProducts(year, make, model);

        if (compatibleProducts.length === 0) {
          showLoading(false);
          showEmpty(true);
          return;
        }

        // Fetch full product data from Shopify
        const productPromises = compatibleProducts
          .filter(p => p.handle)
          .map(p => fetchProductByHandle(p.handle));

        const products = (await Promise.all(productPromises)).filter(p => p !== null);

        if (products.length === 0) {
          showLoading(false);
          showEmpty(true);
          return;
        }

        // Render product grid
        const gridHtml = products.map(p => renderProductCard(p)).join('');
        productGridEl.innerHTML = gridHtml;

        showLoading(false);
        showProductGrid(true);

      } catch (error) {
        console.error('Error loading filtered products:', error);
        showLoading(false);
        showEmpty(true);
      }
    }

    /**
     * Populate filter dropdowns
     */
    async function populateFilters() {
      try {
        // Populate years
        const yearsData = await fetchData('/years');
        const years = yearsData?.range
          ? generateYearRange(yearsData.range.min, yearsData.range.max)
          : generateYearRange(2000, new Date().getFullYear() + 1);

        yearSelect.innerHTML = '<option value="">Year</option>' +
          years.map(y => `<option value="${y}">${y}</option>`).join('');
        yearSelect.disabled = false;

        // Populate makes
        const makesData = await fetchData('/makes');
        if (makesData) {
          const makes = Object.keys(makesData).sort();
          makeSelect.innerHTML = '<option value="">Make</option>' +
            makes.map(m => `<option value="${m}">${m}</option>`).join('');
        }

      } catch (error) {
        console.error('Error populating filters:', error);
      }
    }

    /**
     * Handle year change
     */
    async function handleYearChange() {
      makeSelect.disabled = !yearSelect.value;
      modelSelect.value = '';
      modelSelect.disabled = true;
      applyBtn.disabled = true;

      if (yearSelect.value) {
        makeSelect.disabled = false;
      }
    }

    /**
     * Handle make change
     */
    async function handleMakeChange() {
      modelSelect.value = '';
      applyBtn.disabled = true;

      if (!makeSelect.value) {
        modelSelect.disabled = true;
        return;
      }

      try {
        const makesData = await fetchData('/makes');
        const models = makesData?.[makeSelect.value]?.models || [];

        modelSelect.innerHTML = '<option value="">Model</option>' +
          models.map(m => `<option value="${m}">${m}</option>`).join('');
        modelSelect.disabled = false;

      } catch (error) {
        console.error('Error loading models:', error);
      }
    }

    /**
     * Handle model change
     */
    function handleModelChange() {
      applyBtn.disabled = !modelSelect.value;
    }

    /**
     * Handle filter apply
     */
    function handleFilterApply() {
      const year = yearSelect.value;
      const make = makeSelect.value;
      const model = modelSelect.value;

      if (year && make && model) {
        updateUrl(year, make, model);
        loadFilteredProducts(year, make, model);
      }
    }

    // Event listeners for filters
    yearSelect.addEventListener('change', handleYearChange);
    makeSelect.addEventListener('change', handleMakeChange);
    modelSelect.addEventListener('change', handleModelChange);
    applyBtn.addEventListener('click', handleFilterApply);

    // Initialize
    await populateFilters();

    // Listen for vehicle changes from status bar
    document.addEventListener('vehicle:changed', (e) => {
      const { year, make, model, name } = e.detail;
      if (year && make && model) {
        // Update filter dropdowns
        yearSelect.value = year;
        handleYearChange().then(() => {
          makeSelect.value = make;
          handleMakeChange().then(() => {
            modelSelect.value = model;
            handleModelChange();
            // Update URL and load products
            updateUrl(year, make, model);
            loadFilteredProducts(year, make, model);
          });
        });
      }
    });

    // Also listen for vehicle:saved events
    document.addEventListener('vehicle:saved', (e) => {
      const { year, make, model, name } = e.detail;
      if (year && make && model) {
        // Update filter dropdowns
        yearSelect.value = year;
        handleYearChange().then(() => {
          makeSelect.value = make;
          handleMakeChange().then(() => {
            modelSelect.value = model;
            handleModelChange();
            // Update URL and load products
            updateUrl(year, make, model);
            loadFilteredProducts(year, make, model);
          });
        });
      }
    });

    // Check URL params
    const params = getUrlParams();
    if (params.year && params.make && params.model) {
      // Pre-select filter values
      yearSelect.value = params.year;
      await handleYearChange();
      makeSelect.value = params.make;
      await handleMakeChange();
      modelSelect.value = params.model;
      handleModelChange();

      // Load filtered products
      loadFilteredProducts(params.year, params.make, params.model);
    } else {
      // Show all products (default)
      showAllProducts(true);
      showVehicleInfo(false);
    }
  }

  /**
   * Initialize all vehicle collections on page
   */
  function initAll() {
    const collections = document.querySelectorAll('[data-vehicle-collection]');
    collections.forEach(collection => {
      if (!collection.dataset.initialized) {
        collection.dataset.initialized = 'true';
        initVehicleCollection(collection);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-initialize on Shopify section render
  document.addEventListener('shopify:section:load', initAll);

})();
